import { useEffect, useState } from "react";
import { FiGift, FiInfo } from "react-icons/fi";
import { useQueryClient } from "@tanstack/react-query";
import { BonusCard } from "components/BonusCards/BonusCards";
import { useMutation } from "@tanstack/react-query";
import { AxiosError, AxiosResponse } from "axios";
import { toastrError, toastrSuccess } from "./../../lib/toastr";
import { subscribeBonus, subscribeBonusPoints } from "./Subscription.api";
import { InitialState } from "./BonusPopUp.interface";
import { useAuth } from "context/auth-context";
import { useTranslation } from "react-i18next";

type SubscriptionPoint = typeof InitialState;
type SubscribePayload = Omit<SubscriptionPoint, "errors">;
type ErrorBody = { status: number; message: string };

const BonusPopUp = () => {
  const { auth } = useAuth();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [modalDetails, setModalDetails] = useState(null);
  const [isClicked, setIsClicked] = useState(false);
  const [subscriptionPoint, setSubscriptionPoint] = useState<SubscriptionPoint>(InitialState);
  const [showModal, setShowModal] = useState(true);
  const [hoveredCard, setHoveredCard] = useState(null);
  // const walletDetails: Record<string, number | undefined> = useWalletDetails();
  const points1 = modalDetails?.data[0]?.points;
  const points2 = modalDetails?.data[1]?.points;
  const points3 = modalDetails?.data[2]?.points;

  const targetPoint1 = modalDetails?.data[0]?.target_points;
  const targetPoint2 = modalDetails?.data[1]?.target_points;
  const targetPoint3 = modalDetails?.data[2]?.target_points;

  const planName1 = modalDetails?.data[0]?.planName;
  const planName2 = modalDetails?.data[1]?.planName;
  const planName3 = modalDetails?.data[2]?.planName;

  const type1 = modalDetails?.data[0]?.type;
  const type2 = modalDetails?.data[1]?.type;
  const type3 = modalDetails?.data[2]?.type;

  const subscribeMutation = useMutation(
    (payload: SubscribePayload) => {
      return subscribeBonus(payload.type, payload.points, auth?.panelistId);
    },
    {
      onSuccess: () => {
        toastrSuccess("Request Submit");
        setShowModal(false);
        queryClient.invalidateQueries(["getBonusPoint", auth.panelistId]);
        queryClient.invalidateQueries(["pointBal", auth.panelistId]);
        queryClient.invalidateQueries(["bonusPointsDetails"]);
        queryClient.invalidateQueries(["walletPoints"]);
      },
      onError: (err: AxiosError) => {
        const repsonse = err.response as AxiosResponse<ErrorBody, Record<string, unknown>>;
        toastrError(repsonse?.data.message);
      },
    }
  );

  const BonusPoints = useMutation(
    () => {
      return subscribeBonusPoints();
    },
    {
      onSuccess: (response: any) => {
        if (response.success) {
          setModalDetails(response);
        }
      },
      onError: (err: AxiosError) => {
        const repsonse = err.response as AxiosResponse<ErrorBody, Record<string, unknown>>;
        toastrError(repsonse?.data.message);
      },
    }
  );

  useEffect(() => {
    BonusPoints.mutate();
  }, []);

  const handleSelectPoints = (type: number, points: number) => {
    setSubscriptionPoint({ ...subscriptionPoint, type, points });
    subscribeMutation.mutate({
      type,
      points,
      panelistId: undefined,
    });
    localStorage.removeItem("bonusPopup");
    setIsClicked(true);
    localStorage.setItem("sendNotification", "true");
  };

  const plans = [
    { id: 1, payment: planName1, points: points1, target: targetPoint1, type: type1, featured: hoveredCard === 1 },
    {
      id: 2,
      payment: planName2,
      points: points2,
      target: targetPoint2,
      type: type2,
      featured: !(hoveredCard === 1 || hoveredCard === 3),
    },
    { id: 3, payment: planName3, points: points3, target: targetPoint3, type: type3, featured: hoveredCard === 3 },
  ];

  return (
    <>
      {showModal ? (
        <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="bonus-popup-title">
          <div className="zs-modal !max-w-3xl">
            <div className="flex flex-col items-center px-5 pt-6 pb-2 text-center sm:px-8">
              <span className="inline-flex items-center justify-center w-12 h-12 mb-3 text-2xl rounded-full bg-primary-soft text-primary-text">
                <FiGift />
              </span>
              <span className="text-sm text-ink-3">{t("bonus.popup.your")}</span>
              <h2 id="bonus-popup-title" className="mt-1 text-xl font-bold font-display text-ink">
                {t("bonus.popup.bonus")}
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-3 px-5 py-4 sm:px-8 md:grid-cols-3">
              {plans.map((plan) => (
                <BonusCard
                  key={plan.id}
                  onMouseEnter={() => setHoveredCard(plan.id)}
                  onMouseLeave={() => setHoveredCard(null)}
                  Redeem={t("bonus.popup.Redeem")}
                  SelectPoints={t("bonus.popup.select")}
                  featured={plan.featured}
                  payment={`${plan.payment}`}
                  points={plan.points}
                  FromPoints={`${t("bonus.popup.from")} ${plan.target} ${t("bonus.popup.points")}`}
                  onClickSelectPoints={() => handleSelectPoints(plan.type, plan.points)}
                  disabled={isClicked}
                />
              ))}
            </div>
            <div className="px-5 pb-6 sm:px-8">
              <div className="flex items-start gap-2 p-3 text-xs leading-relaxed border rounded-lg sm:text-sm border-line bg-surface-2 text-ink-2">
                <FiInfo className="mt-0.5 shrink-0 text-info" />
                <div>
                  <span>{t("bonus.popup.Withdraw")}</span> <span>{t("bonus.popup.Points")}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default BonusPopUp;
